import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

/**
 * Views 26 to 31.
 *
 * `connections/new` goes BEFORE `connections/:id` (same reason as `dtes/new`
 * before `dtes/:id` in `dtes.routes.tsx`): with `:id` first, react-router would
 * read "nueva" as an id.
 *
 * ⚠️ `connections` (without an id) has no numbered view of its own in the
 * inventory (§4.9 assigns 28 to the DETAIL only, with `/connections/{id}`
 * endpoints), but the navigation tree (§4.12) sends «Conexiones» to a
 * destination with no id, and `GET /api/integrations/connections` does exist.
 * `ConnectionDetailPage` (the only file this agent may touch for view 28, fixed
 * by §7.2) serves both: with no `:id` in the URL it lists the connections, with
 * `:id` it shows the detail. See that file's JSDoc.
 *
 * Views 29, 30 and 31 are TABS of 28: child routes of `connections/:id`, mounted
 * in `ConnectionDetailPage`'s `<Outlet>`. The index redirects to "documentos" —
 * it is the domain's most actionable tab (§4.9), the one most likely to be what
 * someone is after when opening an existing connection.
 */
export const integrationsRoutes: RouteObject[] = [
  {
    path: 'integrations/connectors',
    lazy: () =>
      import('../pages/ConnectorsCatalogPage').then((m) => ({
        Component: m.ConnectorsCatalogPage,
      })),
  },
  {
    path: 'integrations/connections',
    lazy: () =>
      import('../pages/ConnectionDetailPage').then((m) => ({
        Component: m.ConnectionDetailPage,
      })),
  },
  {
    path: 'integrations/connections/new',
    lazy: () =>
      import('../pages/ConnectionWizardPage').then((m) => ({
        Component: m.ConnectionWizardPage,
      })),
  },
  {
    path: 'integrations/connections/:id',
    lazy: () =>
      import('../pages/ConnectionDetailPage').then((m) => ({
        Component: m.ConnectionDetailPage,
      })),
    children: [
      { index: true, element: <Navigate to="documents" replace /> },
      {
        path: 'mappings',
        lazy: () =>
          import('@/features/integrations/MappingsTab').then((m) => ({
            Component: m.MappingsTab,
          })),
      },
      {
        path: 'documents',
        lazy: () =>
          import('@/features/integrations/DocumentsTab').then((m) => ({
            Component: m.DocumentsTab,
          })),
      },
      {
        path: 'reconciliation',
        lazy: () =>
          import('@/features/integrations/ReconTab').then((m) => ({
            Component: m.ReconTab,
          })),
      },
    ],
  },
];
